import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, X, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useWorkshops } from "@/data/workshops";
import { usePagesContent } from "@/data/pages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom background-position per workshop to ensure dancers are visible
const BG_POSITION = {
  tecnica: "center 65%",
  laboratorio: "center 60%",
  corso_completo: "center center",
};

const buildSessionKey = (level, date) =>
  `${(level || "").toLowerCase()}__${date || ""}`;

export const WorkshopsSection = () => {
  const { workshops } = useWorkshops();
  const { workshops: header } = usePagesContent();
  const [active, setActive] = useState(null);
  const [availability, setAvailability] = useState({});
  const navigate = useNavigate();

  const fetchAvailability = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/workshops/availability`);
      // res.data shape: { [workshopId]: [{level, date, capacity, booked, remaining}, ...] }
      const flat = {};
      Object.entries(res.data || {}).forEach(([wid, sessions]) => {
        sessions.forEach((s) => {
          flat[`${wid}::${buildSessionKey(s.level, s.date)}`] = s;
        });
      });
      setAvailability(flat);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const getSessionInfo = (workshopId, level, date) =>
    availability[`${workshopId}::${buildSessionKey(level, date)}`] || null;

  const goToRegister = (workshopId, level, session) => {
    const params = new URLSearchParams({
      workshop: workshopId,
      level: level.toLowerCase(),
      date: session.date,
      price: session.price,
    });
    navigate(`/register?${params.toString()}`);
  };

  return (
    <section
      id="workshops"
      data-testid="workshops-section"
      className="relative py-28 lg:py-40 bg-[#F7EEDB]"
    >
      {/* Decorative azure accents */}
      <div className="absolute bottom-0 left-0 w-[35vmin] h-[35vmin] rounded-full bg-[#7AB8D6]/25 blur-3xl pointer-events-none" />
      <div className="absolute top-[10%] right-[5%] w-[30vmin] h-[30vmin] rounded-full bg-[#A8D5E5]/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-20"
        >
          <p className="overline text-[#4F8FA8] mb-5">{header.eyebrow}</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#2A1810]">
            {header.heading_line1}
            <br />
            <span className="font-serif-italic text-[#A0522D]">{header.heading_italic}</span>
          </h2>
          {header.description && (
            <p className="mt-6 text-[#6E5B4A] text-base sm:text-lg max-w-xl leading-relaxed">
              {header.description}
            </p>
          )}
        </motion.div>

        <div className="space-y-6">
          {workshops.map((ws, idx) => (
            <motion.button
              key={ws.id}
              data-testid={`workshop-banner-${ws.id}`}
              onClick={() => setActive(ws)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: idx * 0.1 }}
              whileHover={{ scale: 1.005 }}
              className="group relative w-full h-64 sm:h-80 overflow-hidden rounded-sm text-left cursor-pointer block shadow-[0_20px_40px_-20px_rgba(160,82,45,0.2)]"
            >
              <div
                className="absolute inset-0 bg-cover transition-transform duration-700 group-hover:scale-105"
                style={{
                  backgroundImage: `url(${ws.image})`,
                  backgroundPosition: BG_POSITION[ws.id] || "center center",
                }}
              />
              {/* Lighter, sunnier overlay: cream/leather wash instead of dark */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#2A1810]/55 via-[#A0522D]/25 to-[#7AB8D6]/15 group-hover:from-[#2A1810]/40 transition-all" />
              <div className="absolute inset-0 border border-[#FBF6EA]/0 group-hover:border-[#FBF6EA]/30 transition-colors" />

              <div className="relative h-full flex items-center justify-between px-6 sm:px-14">
                <div className="max-w-[80%] sm:max-w-none">
                  <span className="overline text-[#7AB8D6]">0{idx + 1} — Formato</span>
                  <h3 className="font-serif-display text-2xl sm:text-5xl lg:text-6xl text-[#FBF6EA] mt-3 group-hover:translate-x-2 transition-transform duration-500">
                    {ws.title}
                  </h3>
                  <p className="mt-2 text-[#FBF6EA]/85 text-xs sm:text-base">
                    {ws.tagline}
                  </p>
                </div>
                <div className="hidden sm:flex items-center gap-2 overline text-[#FBF6EA] group-hover:text-[#F4C77B] transition">
                  Scopri di più
                  <ChevronRight
                    size={20}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <WorkshopDialog
            workshop={active}
            onClose={() => setActive(null)}
            onSelect={goToRegister}
            getSessionInfo={getSessionInfo}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

const WorkshopDialog = ({ workshop, onClose, onSelect, getSessionInfo }) => {
  return (
    <motion.div
      data-testid="workshop-dialog"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-[#4F8FA8]/40 backdrop-blur-md flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 30 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-[#FBF6EA] border border-[#D9C5A4] rounded-md max-w-[52rem] w-full sm:w-full my-3 sm:my-12 max-h-[calc(100vh-1.5rem)] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden shadow-2xl overscroll-contain"
      >
        <button
          data-testid="dialog-close"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 text-[#FBF6EA] hover:text-[#F4C77B] bg-[#2A1810]/60 backdrop-blur p-2 rounded-full transition"
          aria-label="chiudi"
        >
          <X size={20} />
        </button>

        <div
          className="h-44 sm:h-72 bg-cover bg-center relative"
          style={{ backgroundImage: `url(${workshop.image})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#FBF6EA] via-[#FBF6EA]/30 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 sm:left-8 sm:right-8">
            <p className="overline text-[#A0522D]">{workshop.tagline}</p>
            <h3 className="font-serif-display text-3xl sm:text-5xl text-[#2A1810] mt-2">
              {workshop.title}
            </h3>
          </div>
        </div>

        <div className="p-5 sm:p-10">
          <p className="text-[#4A3B30] leading-relaxed text-base">
            {workshop.description}
          </p>

          <ul className="mt-6 grid sm:grid-cols-2 gap-3">
            {workshop.bullets.map((b) => (
              <li key={b} className="flex gap-3 text-[#4A3B30] text-sm">
                <Check size={18} className="text-[#A0522D] flex-shrink-0 mt-0.5" />
                {b}
              </li>
            ))}
          </ul>

          <div className="mt-10">
            <p className="overline text-[#4F8FA8] mb-5">
              Date · Orari · Prezzi
            </p>
            <div className="grid gap-3">
              {workshop.sessions.map((s) => {
                const info = getSessionInfo
                  ? getSessionInfo(workshop.id, s.level, s.date)
                  : null;
                const remaining = info?.remaining;
                const isSoldOut = remaining === 0;
                const isLow = remaining !== undefined && remaining > 0 && remaining <= 2;
                const isMid = remaining !== undefined && remaining > 2 && remaining <= 5;
                const badgeColor = isSoldOut
                  ? "bg-[#A02525] text-[#FBF6EA]"
                  : isLow
                  ? "bg-[#A0522D] text-[#FBF6EA]"
                  : isMid
                  ? "bg-[#D88E3E] text-[#2A1810]"
                  : "bg-[#4F8FA8]/20 text-[#2A1810] border border-[#4F8FA8]/40";
                return (
                  <div
                    key={`${workshop.id}-${s.level}-${s.date}`}
                    data-testid={`session-${workshop.id}-${s.level.toLowerCase()}`}
                    className={`grid grid-cols-12 gap-4 items-center bg-[#F7EEDB] border border-[#D9C5A4] hover:border-[#A0522D] transition-all rounded-sm p-4 ${
                      isSoldOut ? "opacity-70" : ""
                    }`}
                  >
                    <div className="col-span-12 sm:col-span-3">
                      <p className="overline text-[#6E5B4A]">Livello</p>
                      <p className="font-serif-display text-xl text-[#2A1810]">
                        {s.level}
                      </p>
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      <p className="overline text-[#6E5B4A]">Data</p>
                      <p className="text-[#2A1810] text-sm">{s.date}</p>
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      <p className="overline text-[#6E5B4A]">Orario</p>
                      <p className="text-[#2A1810] text-sm">{s.time}</p>
                      {remaining !== undefined && (
                        <span
                          data-testid={`spots-${workshop.id}-${s.level.toLowerCase()}`}
                          className={`inline-block mt-2 px-2.5 py-1 rounded-full text-[10px] tracking-wider uppercase font-medium ${badgeColor}`}
                        >
                          {isSoldOut
                            ? "Esauriti"
                            : `${remaining} ${
                                remaining === 1 ? "posto" : "posti"
                              } disponibili`}
                        </span>
                      )}
                    </div>
                    <div className="col-span-12 sm:col-span-3 flex sm:justify-end items-center gap-3">
                      <span className="font-serif-display text-2xl text-[#A0522D]">
                        {s.price}
                      </span>
                      <button
                        data-testid={`btn-register-${workshop.id}-${s.level.toLowerCase()}`}
                        onClick={() => !isSoldOut && onSelect(workshop.id, s.level, s)}
                        disabled={isSoldOut}
                        className="btn-primary !py-2.5 !px-4 !text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {isSoldOut ? "Esauriti" : "Iscriviti"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
